import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Database, Trash2, Download } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SEOHead } from "@/components/SEOHead";

const GDPR = () => {
  const { toast } = useToast();
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formData = new FormData(e.currentTarget);
      const name = formData.get("name") as string;
      const email = formData.get("email") as string;
      const requestType = formData.get("requestType") as string;
      const details = formData.get("details") as string;

      const [firstName, ...lastNameParts] = name.split(" ");
      const lastName = lastNameParts.join(" ");

      const trackingPayload = {
        type: "external_form_submission",
        timestamp: Date.now(),
        formId: "GDPR Request Form",
        formData: {
          first_name: firstName || name,
          last_name: lastName || "",
          email: email,
          "contact.gdpr_request_type": requestType,
          "contact.gdpr_details": details,
        },
        formLabels: {
          first_name: "First Name",
          last_name: "Last Name",
          email: "Email",
          "contact.gdpr_request_type": "GDPR Request Type",
          "contact.gdpr_details": "GDPR Details",
        },
        url: window.location.href,
        title: document.title,
        path: window.location.pathname,
        userAgent: navigator.userAgent,
        trackingId: "tk_3efc3dfcd1014878aea33694c30f3120",
        locationId: "KBMLg03QdkeXf4f29Q41",
        sessionId: crypto.randomUUID(),
        properties: {
          deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? "mobile" : "desktop",
        },
      };

      await fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          version: "2021-07-28",
        },
        body: JSON.stringify(trackingPayload),
      });

      toast({
        title: "Request Received",
        description: "We have received your GDPR request and will process it within 30 days.",
      });
      (e.target as HTMLFormElement).reset();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground py-20 px-6">
      <SEOHead
        title="GDPR Data Request"
        description="Submit a request to access, export, or delete your personal data."
        noIndex={true}
      />
      <div className="container mx-auto max-w-3xl">
        <div className="flex items-center gap-3 mb-8">
          <Database className="w-8 h-8 text-primary" />
          <h1 className="text-4xl font-black tracking-tighter">GDPR Data Request</h1>
        </div>
        
        <p className="text-lg text-muted-foreground mb-12">
          Use this form to exercise your rights under the General Data Protection Regulation (GDPR). You can request access to your personal data or ask us to delete it.
        </p>

        <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>Submit a Request</CardTitle>
            <CardDescription>We will respond to your request within 30 days as required by law.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Request Type *</label>
                <Select required name="requestType">
                  <SelectTrigger className="h-12 bg-muted/50">
                    <SelectValue placeholder="Select the type of request" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="access">
                      <div className="flex items-center gap-2">
                        <Download className="w-4 h-4" />
                        <span>Data Access Request (Export my data)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="deletion">
                      <div className="flex items-center gap-2">
                        <Trash2 className="w-4 h-4 text-destructive" />
                        <span>Data Deletion Request (Right to be forgotten)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm font-medium">Full Name *</label>
                  <Input id="name" name="name" required placeholder="Enter your full name" className="h-12 bg-muted/50" />
                </div>
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">Email Address *</label>
                  <Input id="email" name="email" type="email" required placeholder="your@email.com" className="h-12 bg-muted/50" />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="details" className="text-sm font-medium">Additional Details (Optional)</label>
                <Textarea 
                  id="details" 
                  name="details"
                  placeholder="Provide any additional information that might help us identify your data..." 
                  className="min-h-[100px] bg-muted/50 resize-none"
                />
              </div>

              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full h-14 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {isSubmitting ? "Submitting Request..." : "Submit Request"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GDPR;